import { Link, NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Home, Languages, AudioLines, Compass, Library, Download, ShieldCheck, CreditCard, User, LifeBuoy, Settings2, Loader2, AlertTriangle, Inbox } from "lucide-react";
import { useMe } from "../lib/me";
import { NotificationBell } from "./Notifications";

const NAV = [
  ["/", "Home", Home], ["/translate", "Translate", Languages], ["/studio", "Studio", AudioLines], ["/discover", "Discover", Compass],
  ["/library", "Library", Library], ["/exports", "Exports", Download], ["/rights", "Rights", ShieldCheck], ["/billing", "Billing", CreditCard],
  ["/account", "Account", User], ["/help", "Help", LifeBuoy],
];
const MOBILE = NAV.slice(0, 5);

export const Logo = ({ size = 40 }) => (
  <img src="/songape-logo.png" alt="SONGAPE logo" width={size} height={size} style={{ objectFit: "contain", width: size, height: size }} className="rounded-lg" />
);

export function Layout({ children }) {
  const { me } = useMe();
  const isStaff = me && me.role !== "member";
  const link = (to, label, Icon, extra = "") => (
    <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${isActive ? "bg-[#1c2550] text-white" : "muted hover:text-white hover:bg-[#121a3a]"} ${extra}`}>
      <Icon size={18} />{label}
    </NavLink>
  );
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-[var(--border)] p-4 gap-1 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 mb-6 px-1" data-testid="sidebar-logo"><Logo size={44} /><span className="font-bold tracking-[0.18em] text-lg">SONGAPE</span></Link>
        {NAV.map(([to, l, I]) => link(to, l, I))}
        {isStaff && link("/admin", "Administration", Settings2, "mt-3")}
        <div className="mt-auto text-[11px] muted leading-relaxed">Private alpha. Understand. Create. Discover.</div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center justify-between gap-3 px-4 lg:px-8 h-16 border-b border-[var(--border)] sticky top-0 z-30 bg-[#070B1Ccc] backdrop-blur-xl">
          <Link to="/" className="flex lg:hidden items-center gap-2"><Logo size={34} /><span className="font-bold tracking-[0.16em] hidden sm:inline">SONGAPE</span></Link>
          <div className="hidden lg:block text-sm muted">{me ? `Signed in as ${me.display_name}` : ""}</div>
          <div className="flex items-center gap-3">
            {me && <Link to="/billing" className="badge b-info" data-testid="header-credits">{me.credits.available} credits</Link>}
            {isStaff && <Link to="/admin" className="lg:hidden badge b-purple" data-testid="mobile-admin-link">Admin</Link>}
            <Link to="/account" className="lg:hidden muted" aria-label="Account"><User size={20} /></Link>
            <NotificationBell />
            <UserButton />
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-10 pb-28 lg:pb-12 max-w-[1400px] w-full">{children}</main>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B1027f2] backdrop-blur-xl border-t border-[var(--border)] grid grid-cols-5" aria-label="Primary">
        {MOBILE.map(([to, l, I]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`mnav-${l.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] ${isActive ? "text-white" : "muted"}`}>
            <I size={20} />{l}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export const PageHeader = ({ title, sub, actions, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid={testid || "page-title"}>{title}</h1>
      {sub && <p className="muted mt-2 text-sm sm:text-base max-w-2xl">{sub}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Loading = ({ label = "Loading" }) => (
  <div className="flex items-center gap-2 muted py-10 justify-center" data-testid="loading-state"><Loader2 className="spin" size={18} />{label}…</div>
);

export const ErrorState = ({ error, onRetry }) => (
  <div className="card p-6 flex flex-col sm:flex-row sm:items-center gap-3 border-[#ff2d7555]" data-testid="error-state">
    <AlertTriangle className="text-[var(--pink)] shrink-0" />
    <div className="flex-1 text-sm">{error?.message || String(error)}</div>
    {onRetry && <button className="btn btn-ghost btn-sm" onClick={onRetry} data-testid="retry-button">Retry</button>}
  </div>
);

export const Empty = ({ title, children, action, testid = "empty-state" }) => (
  <div className="card p-8 text-center flex flex-col items-center gap-3" data-testid={testid}>
    <Inbox className="muted" /><div className="font-semibold">{title}</div>
    {children && <div className="muted text-sm max-w-md">{children}</div>}{action}
  </div>
);

const TONE = { completed: "b-ok", granted: "b-ok", allow: "b-ok", published: "b-ok", active: "b-ok", paid: "b-ok", resolved: "b-ok", approved_private_alpha: "b-ok",
  failed: "b-bad", denied: "b-bad", deny: "b-bad", rejected: "b-bad", disputed: "b-bad", restricted: "b-bad", cancelled: "b-bad", expired: "b-bad", withdrawn: "b-bad",
  queued: "b-info", running: "b-info", under_review: "b-warn", review: "b-warn", pending_review: "b-warn", open: "b-warn", not_requested: "", cancelling: "b-warn" };
export const Status = ({ value, testid }) => <span className={`badge ${TONE[value] ?? "b-purple"}`} data-testid={testid}>{String(value ?? "—").replace(/_/g, " ")}</span>;

export const Field = ({ label, children, hint }) => (
  <label className="block"><span className="label">{label}</span>{children}{hint && <span className="block text-[11px] muted mt-1">{hint}</span>}</label>
);
